import React from "react";
import { Box, Button, Grid, Link, Typography, useMediaQuery } from "@mui/material";
import DOMPurify from "dompurify";
import { useAppLoadingContext } from "../../Context/AppLoadingContext";
import { AppLoadingContextTypes } from "../../Context/ContextTypes/AppLoadingContextTypes";
import "react-lazy-load-image-component/src/effects/blur.css";
import { LazyLoadImage } from "react-lazy-load-image-component";
import { useTheme } from "@mui/material/styles";
import FourthVariant from "./components/FourthVariant/FourthVariant";

type StartEarningPropTypes = {
  earnCardId: string;
  earnCardBorderRadiusTop: string;
  earnCardBorderRadiusBottom: string;
  earnCardVariant: string;
  earnCardHeaderText: string;
  earnCardButtonText: string;
  earnCardButtonUrl: string;
  earnCardButtonAriaLabel: string;
  earnCardButtonId: string;
  earnCardSubHeader: string;
  earnCardSubParagraph: string;
  earnCardSubLink: string;
  earnCardSubLinkUrl: string;
  earnCardSubLinkId: string;
  earnCardButtonImageItems: Array<{
    itemImageSrc: string;
    itemImageHeight: string;
    itemImageWidth: string;
    itemImageAltText: string;
    itemLinkUrl: string;
    itemLinkAriaLabel: string;
  }>;
  earnCardImage: string;
  earnCardImageWidth: string;
  earnCardImageHeight: string;
  earnCardImageLeftRightMargin: string;
  earnCardImageAltText: string;
  isInEditor: boolean;
  cqPath: string;
  cqType: string;
};
export default function StartEarningReact(props: StartEarningPropTypes) {
  // translation
  const { translationsData } = useAppLoadingContext() as AppLoadingContextTypes;
  const DomPurify = (tag: string) => {
    return DOMPurify.sanitize(translationsData[tag]);
  };

  const translatedData = {
    // title and button
    earnCardHeaderText: DomPurify(props.earnCardHeaderText),
    earnCardButtonText: DomPurify(props.earnCardButtonText),
    earnCardButtonUrl: DomPurify(props.earnCardButtonUrl),
    earnCardButtonAriaLabel: DomPurify(props.earnCardButtonAriaLabel),
    earnCardSubHeader: DomPurify(props.earnCardSubHeader),
    earnCardSubParagraph: DomPurify(props.earnCardSubParagraph),
    earnCardSubLink: DomPurify(props.earnCardSubLink),
    earnCardSubLinkUrl: DomPurify(props.earnCardSubLinkUrl),
    earnCardImage: DomPurify(props.earnCardImage),
    earnCardImageWidth: props.earnCardImageWidth,
    earnCardImageHeight: props.earnCardImageHeight,
    earnCardImageAltText: DomPurify(props.earnCardImageAltText),
  };

  const isMobile = useMediaQuery(useTheme().breakpoints.down("sm"));
  // const isMobileAndTablet = useMediaQuery(useTheme().breakpoints.down("lg"));
  // const isDesktop = useMediaQuery(useTheme().breakpoints.up("lg"));

  const isFourthVariant = props.earnCardVariant === "4";

  if (isFourthVariant) {
    return <FourthVariant {...props} />;
  }

  //else return default view, which is for Variants 1, 2, 3
  return (
    <Box
      component="section"
      id={props.earnCardId}
      sx={{
        margin: "40px 0",
        width: "100%",
        display: "flex",
        justifyContent: "center",
      }}
    >
      <Grid
        container
        display="flex"
        flexDirection="row"
        justifyContent="center"
        maxWidth={{
          xs: "100%",
          sm: props.earnCardVariant === "2" ? "auto" : "750px",
          md: props.earnCardVariant === "2" ? "100%" : "90%",
          lg: "1296px",
        }}
        marginLeft={{ xs: "0px", sm: props.earnCardVariant === "2" ? "37px" : "auto", lg: "auto" }}
        marginRight={{ xs: "0px", sm: props.earnCardVariant === "2" ? "37px" : "auto", lg: "auto" }}
        sx={{
          paddingTop:'30px',
          backgroundColor: "#F8F8F8",
          // borderRadius: props.earnCardVariant === "2" ? "" : "30px",
          borderRadius:
            props.earnCardVariant === "2"
              ? isMobile
                ? "0px"
                : props.earnCardBorderRadiusTop
              : "30px",
        }}
      >
        <Grid
          item
          xs={12}
          display="flex"
          sx={{
            flexFlow: {
              xs: props.earnCardVariant === "3" ? "column-reverse" : "",
              lg: props.earnCardVariant === "3" ? "wrap" : "",
            },
          }}
          direction={{ xs: "column", lg: "row" }}
        >
          <Grid item xs={12} sm={12} md={12} lg={6}>
            <Grid
              container
              direction="column"
              padding={{
                xs: props.earnCardVariant === "2" ? "0px 14px 0px 14px" : "0px 20px 0px 20px",
                sm: "0px 20px 0px 20px",
                md: "0px 40px 0px 40px",
                lg: "0px 0px 0px 104px",
              }}
            >
              {/* title */}
              <Typography
                variant="h2"
                display="block"
                fontWeight="700"
                textAlign={{ xs: "center", sm: "center", md: "center", lg: "left" }}
                paddingTop={{ xs: "30px", sm: "30px", md: "30px", lg: "60px" }}
                marginTop={{ xs: "10px", sm: "10px", md: "10px", lg: "0px" }}
                lineHeight="44px"
                paddingLeft={{ xs: "0", sm: "15%", md: "15%", lg: "0px" }}
                paddingRight={{ xs: "0", sm: "15%", md: "15%", lg: "0px" }}
                fontSize={{ xs: "28px", sm: "36px", lg: "2.25rem" }}
              >
                {translatedData.earnCardHeaderText}
              </Typography>

              {(props.earnCardVariant === "1" || props.earnCardVariant === "3") && (
                <Grid
                  item
                  textAlign={{ xs: "center", sm: "center", md: "center", lg: "left" }}
                  sx={{ marginBottom: "20px" }}
                >
                  <Button
                    component={Link}
                    variant="contained"
                    id={props.earnCardButtonId || `startEarningApplyNowBtn`}
                    sx={{
                      padding: "0.7rem 1rem 0.5rem 1rem",
                      marginTop: "36px",
                      minWidth: { xs: "auto%", sm: "50%", md: "50%", lg: "auto" },
                      whiteSpace: "nowrap",
                      // width: "auto !important",
                      marginBottom: props.earnCardVariant === "3" ? "20px" : "",
                    }}
                    href={translatedData.earnCardButtonUrl}
                    className="primary-btn"
                  >
                    {translatedData.earnCardButtonText}
                  </Button>
                </Grid>
              )}

              {translatedData.earnCardSubHeader ? (
                <Typography
                  variant="body1"
                  display="block"
                  fontWeight="700"
                  fontSize={
                    props?.earnCardVariant === "1" || props?.earnCardVariant === "3"
                      ? "1.5rem"
                      : { xs: "18px", lg: "24px" }
                  }
                  textTransform="capitalize"
                  textAlign={{ xs: "center", sm: "center", md: "center", lg: "left" }}
                  paddingTop="20px"
                  borderTop={
                    props?.earnCardVariant === "1" || props?.earnCardVariant === "3"
                      ? "solid 2px #E7E8E8"
                      : "none"
                  }
                  marginLeft={{ xs: "3%", sm: "3%", md: "3%", lg: "0%" }}
                  marginRight={{ xs: "3%", sm: "3%", md: "3%", lg: "0%" }}
                >
                  {translatedData.earnCardSubHeader}
                </Typography>
              ) : null}

              {translatedData.earnCardSubParagraph ? (
                <Typography
                  variant="body1"
                  display="block"
                  fontSize={props.earnCardVariant === "2" ? "16px" : ""}
                  textAlign={{ xs: "center", sm: "center", md: "center", lg: "left" }}
                  paddingTop="20px"
                  paddingBottom={
                    props?.earnCardVariant === "2" ? "20px !important" : "0px !important"
                  }
                  borderBottom={props?.earnCardVariant === "2" ? "solid 2px #E7E8E8" : "none"}
                  padding={{
                    xs: props?.earnCardVariant === "2" ? "8px 0px 23px 0px" : "30px 35px 0px",
                    sm: props?.earnCardVariant === "2" ? "8px 50.5px 23px 50.5px" : "20px 36% 0px",
                    lg: "20px 0px 0px",
                  }}
                  sx={{ lineHeight: props?.earnCardVariant === "2" ? "24px" : "" }}
                >
                  {translatedData.earnCardSubParagraph}
                </Typography>
              ) : null}

              {translatedData.earnCardSubLink &&
              (props?.earnCardVariant === "1" || props?.earnCardVariant === "3") ? (
                <Grid
                  item
                  display="flex"
                  alignItems="center"
                  textAlign={{ xs: "center", sm: "center", md: "center", lg: "left" }}
                  paddingTop="20px"
                  paddingBottom="30px"
                  justifyContent={{ xs: "center", sm: "center", md: "center", lg: "left" }}
                >
                  <Typography variant="body1" fontWeight="700" component={Link}>
                    <Link
                      id={props.earnCardSubLinkId || `startEarningCompareCreditCardLink`}
                      href={translatedData.earnCardSubLinkUrl}
                      color="#0b508e"
                    >
                      {translatedData.earnCardSubLink}
                    </Link>
                  </Typography>
                  <LazyLoadImage
                    effect="blur"
                    delayTime={1000}
                    src="https://media.ctfs.com/pages/CRECRD/Chevron-Right.svg"
                    alt=""
                    width="24"
                    height="24"
                    style={{ marginLeft: "15px" }}
                  />
                </Grid>
              ) : null}

              {props?.earnCardVariant === "2" ? (
                <Grid
                  item
                  container
                  display="flex"
                  alignItems="center"
                  textAlign={{ xs: "center", lg: "left" }}
                  justifyContent={{ xs: "center", lg: "left" }}
                  paddingTop="30px"
                  paddingBottom="20px"
                  gap={2}
                >
                  {props?.earnCardButtonImageItems?.map((item) => (
                    <Grid item>
                      <a
                        href={DomPurify(item?.itemLinkUrl)}
                        aria-label={DomPurify(item?.itemLinkAriaLabel)}
                        target="_blank"
                        rel="noreferrer"
                      >
                        <LazyLoadImage
                          effect="blur"
                          delayTime={1000}
                          src={DomPurify(item?.itemImageSrc)}
                          alt={DomPurify(item?.itemImageAltText)}
                          height={item?.itemImageHeight}
                          width={item?.itemImageWidth}
                          style={{ display: "flex" }}
                        />
                      </a>
                    </Grid>
                  ))}
                </Grid>
              ) : null}
            </Grid>
          </Grid>

          {/* card image container */}
          <Grid
            item
            xs={12}
            sm={12}
            md={12}
            lg={6}
            display="flex"
            justifyContent="center"

            sx={{
              alignItems: "center",
              marginTop: props.earnCardVariant === "3" ? "20px" : "",
              padding:{xs:'10px 12px 0px 12px', sm:"0px", lg:"0px"}
            }}
          >
            <Grid container direction="column">
              <Grid
                item
                margin={{ xs: "0px", sm: "0px", md: "0px", lg: "28px 0 0" }}
                display="flex"
                justifyContent="center"
              >
                <LazyLoadImage
                  effect="blur"
                  delayTime={1000}
                  src={translatedData.earnCardImage}
                  width={isMobile ? "auto" : translatedData.earnCardImageWidth}
                  height={isMobile ? "auto" : translatedData.earnCardImageHeight}
                  alt=""
                  style={{
                    display: "flex",
                    padding: `0px ${props.earnCardImageLeftRightMargin}`,
                  }}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        {/* triangle color borders */}
        <Grid item xs={12} sm={12} md={12} lg={12}>
          <Box
            component="div"
            className="trianleBgBorder"
            sx={{
              borderBottomLeftRadius: props.earnCardBorderRadiusBottom
                ? {
                    xs: "0px",
                    sm: props.earnCardBorderRadiusBottom,
                    lg: props.earnCardBorderRadiusBottom,
                  }
                : { xs: "0px", sm: "10px", lg: "30px" },
              
              borderBottomRightRadius: props.earnCardBorderRadiusBottom
                ? {
                    xs: "0px",
                    sm: props.earnCardBorderRadiusBottom,
                    lg: props.earnCardBorderRadiusBottom,
                  }
                : { xs: "0px", sm: "10px", lg: "30px" },
            }}
          />
        </Grid>
      </Grid>
    </Box>
  );
}
